import React from 'react';
import {
  Box,
  VStack,
  Text,
  Flex,
  Badge,
  IconButton,
  useColorModeValue,
  HStack,
  Tooltip
} from '@chakra-ui/react';
import { ThemeTemplate } from '../context/LyricsThemeContext';
import { StarIcon, EditIcon, DeleteIcon } from './IconComponents';

interface TemplatePreviewProps {
  template: ThemeTemplate;
  isSelected: boolean;
  isDefault: boolean;
  onClick: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onSetDefault: () => void;
  accentColor: string;
}

/**
 * Component that shows a preview of a lyrics template
 */
const TemplatePreview: React.FC<TemplatePreviewProps> = ({
  template,
  isSelected,
  isDefault,
  onClick,
  onEdit,
  onDelete,
  onSetDefault,
  accentColor
}) => {
  const {
    name,
    theme,
    colorMode,
    backgroundColor,
    backgroundType
  } = template;

  // Colors and styles
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const selectedBorderColor = `${accentColor}.400`;
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedTextColor = useColorModeValue('gray.500', 'gray.400');
  
  // Sample lyrics for preview
  const sampleLyrics = ['Past lyric line', 'Current lyric line', 'Future lyric line'];
  
  return (
    <Box
      bg={bgColor}
      borderWidth="1px"
      borderRadius="md"
      borderColor={isSelected ? selectedBorderColor : borderColor}
      overflow="hidden"
      position="relative"
      cursor="pointer"
      transition="all 0.2s"
      _hover={{ 
        transform: 'translateY(-2px)', 
        boxShadow: 'md',
        borderColor: isSelected ? selectedBorderColor : `${accentColor}.200`
      }}
      onClick={onClick}
      height="220px"
    >
      {/* Background preview */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        zIndex={0}
        bg={backgroundType === 'solid' ? backgroundColor : 'transparent'}
        backgroundImage={backgroundType === 'gradient' ? 
          `linear-gradient(45deg, ${backgroundColor || '#121212'} 0%, ${colorMode === 'dark' ? '#1f1f1f' : '#f8f8f8'} 100%)` 
          : 'none'
        }
        opacity={0.8}
      />
      
      {/* Default badge */}
      {isDefault && (
        <Badge
          position="absolute"
          top={2}
          right={2}
          zIndex={2}
          colorScheme={accentColor}
          variant="solid"
          fontSize="xs"
          borderRadius="sm"
          px={2}
        >
          Default
        </Badge>
      )}
      
      {/* Content */}
      <VStack
        position="relative"
        zIndex={1}
        p={4}
        align="stretch"
        height="100%"
        spacing={3}
        justify="space-between"
      >
        <Text
          fontWeight="bold"
          fontSize="md"
          color={textColor}
          noOfLines={1}
        >
          {name}
        </Text>
        
        {/* Sample lyrics preview */}
        <Flex direction="column" justify="center" flex="1">
          <VStack 
            spacing={template.lineSpacing * 4} 
            align="center"
          >
            {sampleLyrics.map((line, i) => (
              <Text
                key={i}
                fontSize={`${Math.round(12 * template.fontSize)}px`}
                opacity={theme === 'spotify' && i === 0 ? 0.5 : i === 1 ? 1 : theme === 'spotify' ? 0.8 : 0.6}
                fontWeight={i === 1 ? 'bold' : 'normal'}
                color={i === 1 ? template.activeTextColor : template.inactiveTextColor}
                bg={i === 1 && theme === 'default' ? `${accentColor}.50` : 'transparent'}
                px={i === 1 && theme === 'default' ? 2 : 0}
                py={i === 1 && theme === 'default' ? 1 : 0}
                borderRadius={i === 1 && theme === 'default' ? 'md' : 0}
              >
                {line}
              </Text>
            ))}
          </VStack>
        </Flex>
        
        {/* Template info */}
        <HStack justify="space-between" align="center">
          <Text fontSize="xs" color={mutedTextColor}>
            {theme === 'spotify' ? 'Spotify Style' : 'Default Style'}
          </Text>
          <Text fontSize="xs" color={mutedTextColor}>
            {colorMode === 'dark' ? 'Dark' : 'Light'}
          </Text>
        </HStack>
        
        {/* Action buttons */}
        <HStack justify="center" mt={2} spacing={1}>
          {!isDefault && (
            <Tooltip label="Set as default">
              <IconButton
                aria-label="Set as default"
                icon={<StarIcon />}
                size="sm"
                colorScheme={accentColor}
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  onSetDefault();
                }}
              />
            </Tooltip>
          )}
          <Tooltip label="Edit template">
            <IconButton
              aria-label="Edit template"
              icon={<EditIcon />}
              size="sm"
              colorScheme={accentColor}
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
            />
          </Tooltip>
          {!isDefault && (
            <Tooltip label="Delete template">
              <IconButton
                aria-label="Delete template"
                icon={<DeleteIcon />}
                size="sm"
                colorScheme="red"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
              />
            </Tooltip>
          )}
        </HStack>
      </VStack>
    </Box>
  );
};

export default TemplatePreview; 